import { useState, useEffect } from 'react';
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  doc,
  getDoc,
} from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import {
  DollarSign,
  TrendingUp,
  Calendar,
  FileText,
  ArrowDown,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Info,
  Landmark,
  ShieldAlert,
} from 'lucide-react';

const Fees = () => {
  const [feeAccount, setFeeAccount] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dataSource, setDataSource] = useState('transactions');
  const [stats, setStats] = useState({
    totalFees: 0,
    totalTransactions: 0,
    feesThisMonth: 0,
    feesLastMonth: 0,
  });
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  const [withdrawStatus, setWithdrawStatus] = useState(null);

  const quickAmounts = [25, 50, 100, 200, 500];

  const FEE_ACCOUNT_EMAIL = 'flexyswap@gmail.com';
  const userInfoCache = new Map();

  useEffect(() => {
    fetchFeeData();
  }, []);

  const getUserInfo = async userId => {
    if (!userId) return null;
    if (userInfoCache.has(userId)) {
      return userInfoCache.get(userId);
    }

    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const info = {
          id: userId,
          email: userData.email,
          displayName:
            userData.displayName ||
            `${userData.firstName || ''} ${userData.lastName || ''}`.trim() ||
            userData.email ||
            'Unknown User',
        };
        userInfoCache.set(userId, info);
        return info;
      }
    } catch (error) {
      console.warn('Error fetching user info:', error);
    }

    return null;
  };

  const fetchFeeData = async () => {
    try {
      setLoading(true);

      // Find fee account by email
      const usersSnapshot = await getDocs(
        query(collection(db, 'users'), where('email', '==', FEE_ACCOUNT_EMAIL)),
      );

      if (usersSnapshot.empty) {
        console.warn('Fee account not found:', FEE_ACCOUNT_EMAIL);
        setLoading(false);
        return;
      }

      const feeAccountDoc = usersSnapshot.docs[0];
      const feeAccountData = feeAccountDoc.data();
      const feeAccountId = feeAccountDoc.id;

      setFeeAccount({
        id: feeAccountId,
        email: feeAccountData.email,
        walletBalance: feeAccountData.walletBalance || 0,
        displayName:
          feeAccountData.displayName ||
          feeAccountData.firstName + ' ' + feeAccountData.lastName ||
          'FlexySwap Fee Account',
      });

      // Fetch all fee transactions
      let transactionsSnapshot;
      try {
        // Try with orderBy first (for proper sorting)
        transactionsSnapshot = await getDocs(
          query(
            collection(db, 'wallet_transactions', feeAccountId, 'transactions'),
            orderBy('date', 'desc'),
          ),
        );
      } catch (orderByError) {
        // If orderBy fails (e.g., missing index), fetch without orderBy
        console.warn('orderBy failed, fetching without orderBy:', orderByError);
        transactionsSnapshot = await getDocs(
          collection(db, 'wallet_transactions', feeAccountId, 'transactions'),
        );
      }

      const feeTransactionsList = [];
      const withdrawalTransactionsList = [];
      let totalFees = 0;
      let feesThisMonth = 0;
      let feesLastMonth = 0;
      let migratedFeesTotal = 0;
      let nonMigratedFeesTotal = 0;

      const now = new Date();
      const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);

      console.log(
        `📊 Fetched ${transactionsSnapshot.docs.length} transactions from fee account`,
      );

      // Debug: Log all transaction types and descriptions
      const transactionTypes = {};
      const transactionDescriptions = new Set();
      transactionsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const type = data.type || 'unknown';
        transactionTypes[type] = (transactionTypes[type] || 0) + 1;
        if (data.description) {
          transactionDescriptions.add(data.description.toLowerCase());
        }
      });
      console.log('📋 Transaction types:', transactionTypes);
      console.log(
        '📝 Unique descriptions:',
        Array.from(transactionDescriptions).slice(0, 10),
      );

      for (const transDoc of transactionsSnapshot.docs) {
        const transData = transDoc.data();

        // Handle date field - support both Timestamp and regular Date
        let transactionDate;
        if (transData.date) {
          if (transData.date?.toDate) {
            transactionDate = transData.date.toDate();
          } else if (transData.date instanceof Date) {
            transactionDate = transData.date;
          } else if (typeof transData.date === 'number') {
            transactionDate = new Date(transData.date);
          } else if (typeof transData.date === 'string') {
            transactionDate = new Date(transData.date);
          } else {
            // Fallback: use current date if date is invalid
            transactionDate = new Date();
          }
        } else {
          // If no date field, use a very old date so it doesn't affect monthly calculations
          transactionDate = new Date(0);
        }

        // Check if it's a fee transaction
        const description = transData.description?.toLowerCase() || '';
        const isFeeTransaction =
          transData.type === 'earning' &&
          (description.includes('flexyswap fee') ||
            description.includes('fee collected') ||
            description.includes('flexy fee'));

        // Debug: Log transactions that should be fees but aren't being recognized
        if (transData.type === 'earning' && !isFeeTransaction) {
          console.warn('⚠️ Earning transaction not recognized as fee:', {
            id: transDoc.id,
            type: transData.type,
            description: transData.description,
            amount: transData.amount,
          });
        }

        // Check if it's a withdrawal transaction
        const isWithdrawalTransaction = transData.type === 'withdrawal';

        if (isFeeTransaction) {
          const amount = Math.abs(transData.amount || 0);
          totalFees += amount;

          // Track migrated vs non-migrated fees
          if (transData.migrated) {
            migratedFeesTotal += amount;
            console.log('✅ Found migrated transaction:', {
              id: transDoc.id,
              amount: amount,
              description: transData.description,
              date: transactionDate,
            });
          } else {
            nonMigratedFeesTotal += amount;
          }

          // Calculate monthly fees
          if (transactionDate >= thisMonthStart) {
            feesThisMonth += amount;
          } else if (
            transactionDate >= lastMonthStart &&
            transactionDate <= lastMonthEnd
          ) {
            feesLastMonth += amount;
          }

          feeTransactionsList.push({
            id: transDoc.id,
            amount: amount,
            description: transData.description || 'Fee transaction',
            date: transactionDate,
            status: transData.status || 'completed',
            requestId: transData.requestId,
            type: transData.type,
          });
        } else if (isWithdrawalTransaction) {
          const amount = Math.abs(transData.amount || 0);
          withdrawalTransactionsList.push({
            id: transDoc.id,
            amount: amount,
            description: transData.description || 'Withdrawal',
            date: transactionDate,
            status: transData.status || 'completed',
            payoutId: transData.payoutId,
            simulated: transData.simulated,
            type: transData.type,
          });
        }
      }

      // Fetch payer information for fee transactions with requestId
      const feeTransactionsWithPayerInfo = await Promise.all(
        feeTransactionsList.map(async transaction => {
          let payerInfo = null;
          if (transaction.requestId) {
            try {
              const requestDoc = await getDoc(
                doc(db, 'requests', transaction.requestId),
              );
              if (requestDoc.exists()) {
                const requestData = requestDoc.data();
                const payerId = requestData.requesterId;

                if (payerId) {
                  payerInfo = await getUserInfo(payerId);
                }

                if (!payerInfo) {
                  payerInfo = {
                    id: payerId || null,
                    email:
                      requestData.requesterEmail ||
                      requestData.tripOwnerEmail ||
                      'Unknown',
                    displayName:
                      requestData.requesterName ||
                      requestData.tripOwnerName ||
                      requestData.requesterEmail ||
                      'Unknown User',
                  };
                }
              }
            } catch (error) {
              console.warn('Error fetching payer info:', error);
            }
          }
          return {
            ...transaction,
            payerInfo: payerInfo,
          };
        }),
      );

      console.log('💰 Fee calculation summary:', {
        totalFees: totalFees,
        migratedFeesTotal: migratedFeesTotal,
        nonMigratedFeesTotal: nonMigratedFeesTotal,
        feeTransactionsCount: feeTransactionsList.length,
        withdrawalTransactionsCount: withdrawalTransactionsList.length,
        feesThisMonth: feesThisMonth,
        feesLastMonth: feesLastMonth,
      });

      if (
        feeTransactionsWithPayerInfo.length > 0 ||
        withdrawalTransactionsList.length > 0
      ) {
        setTransactions(feeTransactionsWithPayerInfo);
        setWithdrawals(withdrawalTransactionsList);
        setStats({
          totalFees: totalFees,
          totalTransactions:
            feeTransactionsList.length + withdrawalTransactionsList.length,
          feesThisMonth: feesThisMonth,
          feesLastMonth: feesLastMonth,
        });
        setDataSource('transactions');
      } else {
        const historicalResult = await fetchHistoricalFeesFromRequests();
        setTransactions(historicalResult.transactions);
        setWithdrawals([]);
        setStats({
          totalFees: historicalResult.totalFees,
          totalTransactions: historicalResult.transactions.length,
          feesThisMonth: historicalResult.feesThisMonth,
          feesLastMonth: historicalResult.feesLastMonth,
        });
        setDataSource('requests');
        // Calculate pending migration for historical data too
        setPendingMigrationAmount(0); // Already calculated from requests
      }
    } catch (error) {
      console.error('Error fetching fee data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistoricalFeesFromRequests = async () => {
    const snapshot = await getDocs(collection(db, 'requests'));
    const history = [];
    let totalFees = 0;
    let feesThisMonth = 0;
    let feesLastMonth = 0;

    const now = new Date();
    const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);

    for (const requestDoc of snapshot.docs) {
      const requestData = requestDoc.data();
      const status = (requestData.status || '').toLowerCase();
      if (!['completed', 'delivered'].includes(status)) {
        continue;
      }

      const price = parseFloat(
        requestData.price || requestData.estimatedPrice || 0,
      );
      if (!price || price <= 0) {
        continue;
      }

      const feeAmount = Number((price * 0.15).toFixed(2));
      totalFees += feeAmount;

      const completionDate = requestData.completedAt?.toDate
        ? requestData.completedAt.toDate()
        : requestData.updatedAt?.toDate
        ? requestData.updatedAt.toDate()
        : new Date(
            requestData.updatedAt || requestData.createdAt || Date.now(),
          );

      if (completionDate >= thisMonthStart) {
        feesThisMonth += feeAmount;
      } else if (
        completionDate >= lastMonthStart &&
        completionDate <= lastMonthEnd
      ) {
        feesLastMonth += feeAmount;
      }

      let payerInfo = await getUserInfo(requestData.requesterId);
      if (!payerInfo) {
        payerInfo = {
          id: requestData.requesterId || null,
          email: requestData.requesterEmail || 'Unknown',
          displayName:
            requestData.requesterName ||
            requestData.requesterEmail ||
            'Unknown User',
        };
      }

      history.push({
        id: `${requestDoc.id}-historical`,
        amount: feeAmount,
        description: `Calculated fee from request: ${
          requestData.itemTitle || 'Item'
        }`,
        date: completionDate,
        status: status,
        requestId: requestDoc.id,
        payerInfo,
        calculated: true,
      });
    }

    history.sort((a, b) => b.date - a.date);

    return {
      transactions: history,
      totalFees,
      feesThisMonth,
      feesLastMonth,
    };
  };

  const formatCurrency = amount => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = date => {
    if (!date) return 'N/A';
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const handleQuickAmount = value => {
    if (value <= feeAccount.walletBalance) {
      setWithdrawAmount(value.toString());
      setWithdrawStatus(null);
    } else {
      setWithdrawStatus({
        type: 'error',
        message: `Insufficient balance. Available: ${formatCurrency(
          feeAccount.walletBalance,
        )}`,
      });
    }
  };

  const handleWithdraw = async event => {
    if (event) event.preventDefault();
    const amount = parseFloat(withdrawAmount);
    if (!amount || amount <= 0) {
      setWithdrawStatus({
        type: 'error',
        message: 'Please enter a valid amount',
      });
      return;
    }

    if (amount > feeAccount.walletBalance) {
      setWithdrawStatus({
        type: 'error',
        message: `Insufficient balance. Available: ${formatCurrency(
          feeAccount.walletBalance,
        )}`,
      });
      return;
    }

    if (
      !confirm(
        `Are you sure you want to withdraw ${formatCurrency(
          amount,
        )} to the bank account?`,
      )
    ) {
      return;
    }

    setIsWithdrawing(true);
    setWithdrawStatus(null);
    try {
      const user = auth.currentUser;
      if (!user) {
        setWithdrawStatus({
          type: 'error',
          message: 'Please log in first',
        });
        return;
      }

      const authToken = await user.getIdToken();
      const functionUrl = `https://us-central1-${
        import.meta.env.VITE_FIREBASE_PROJECT_ID || 'flexyswap'
      }.cloudfunctions.net/withdrawFeeAccountBalance`;

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount,
          authToken,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Withdrawal failed');
      }

      const arrivalDate = result.payout.arrivalDate
        ? new Date(result.payout.arrivalDate * 1000).toLocaleDateString()
        : 'N/A';

      setWithdrawStatus({
        type: 'success',
        message: `Withdrawal successful! Payout ID: ${result.payout.id.substring(
          0,
          12,
        )}... Status: ${result.payout.status}. Arrival: ${arrivalDate}`,
      });
      setWithdrawAmount('');
      await fetchFeeData(); // Refresh data
    } catch (error) {
      console.error('Withdrawal error:', error);
      setWithdrawStatus({
        type: 'error',
        message: `Withdrawal failed: ${error.message}`,
      });
    } finally {
      setIsWithdrawing(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading fee data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!feeAccount) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">
            Fee account not found. Please ensure the account with email{' '}
            <strong>{FEE_ACCOUNT_EMAIL}</strong> exists.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Fee Management</h1>
        <p className="text-gray-600 mt-2">
          View and manage all FlexySwap platform fees
        </p>
      </div>

      {dataSource === 'requests' && (
        <div className="mb-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              Showing historical fees calculated from completed requests. Once
              new fee transactions are recorded, this view will automatically
              switch to live wallet data.
            </p>
          </div>
        </div>
      )}

      {/* Account Balance Card */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-700 mb-1">
              Fee Account Balance
            </h2>
            <p className="text-sm text-gray-500 mb-4">{feeAccount.email}</p>
            <div>
              <p className="text-3xl font-bold text-yellow-600">
                {formatCurrency(feeAccount.walletBalance)}
              </p>
              <p className="text-sm text-gray-500">Current Balance</p>
            </div>
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-700 mb-1">
              Available to Withdraw
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              Total amount available for withdrawal
            </p>
            <div>
              <p className="text-3xl font-bold text-green-600">
                {formatCurrency(feeAccount.walletBalance)}
              </p>
              <p className="text-sm text-gray-500">Withdrawable Amount</p>
            </div>
          </div>
        </div>

        {/* Withdrawal Form */}
        {feeAccount && (
          <div className="border-t border-gray-200 pt-6 mt-6">
            <form onSubmit={handleWithdraw} className="space-y-6">
              {/* Account Status */}
              <div className="flex items-center gap-3 p-4 rounded-2xl bg-green-50 border border-green-200">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                <p className="text-sm font-semibold text-green-700">
                  Platform Account Ready for Withdrawals
                </p>
              </div>

              {/* Account Information Card */}
              <div className="rounded-2xl border border-gray-200 bg-gray-50 p-6 space-y-4">
                <div className="flex items-center gap-3">
                  <Landmark className="h-5 w-5 text-green-600" />
                  <h3 className="text-lg font-semibold text-gray-900">
                    Withdrawal Account
                  </h3>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Account Type:</span>
                    <span className="text-sm font-semibold text-gray-900">
                      Platform Bank Account
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Payouts:</span>
                    <span className="text-sm font-semibold text-green-600">
                      Enabled
                    </span>
                  </div>
                </div>
                <p className="text-sm text-gray-600">
                  Funds will be sent to the platform's default Stripe bank
                  account via payout.
                </p>
              </div>

              {/* Amount Input */}
              <div>
                <p className="text-sm font-semibold text-gray-500 uppercase tracking-widest mb-3">
                  Withdrawal Amount
                </p>

                <div className="flex items-center gap-3 rounded-2xl border-2 border-gray-200 bg-gray-50 px-4 py-4 focus-within:ring-2 focus-within:ring-yellow-300 focus-within:bg-white">
                  <span className="text-2xl font-semibold text-gray-900">
                    USD
                  </span>
                  <input
                    type="number"
                    min="1"
                    step="0.01"
                    placeholder="0.00"
                    value={withdrawAmount}
                    onChange={event => {
                      setWithdrawAmount(event.target.value);
                      setWithdrawStatus(null);
                    }}
                    className="flex-1 bg-transparent text-2xl font-semibold text-gray-900 placeholder:text-gray-400 focus:outline-none"
                  />
                </div>

                {/* Quick Amounts */}
                <div className="mt-4">
                  <p className="text-sm font-semibold text-gray-500 uppercase tracking-widest mb-3">
                    Quick Amounts
                  </p>
                  <div className="flex flex-wrap gap-3">
                    {quickAmounts.map(value => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => handleQuickAmount(value)}
                        disabled={value > feeAccount.walletBalance}
                        className={`rounded-xl border px-4 py-2 text-sm font-semibold transition ${
                          Number(withdrawAmount) === value
                            ? 'border-yellow-400 bg-yellow-50 text-yellow-600'
                            : value > feeAccount.walletBalance
                            ? 'border-gray-100 bg-gray-50 text-gray-400 cursor-not-allowed'
                            : 'border-gray-200 bg-white text-gray-700 hover:border-yellow-300'
                        }`}
                      >
                        {formatCurrency(value)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Status Message */}
              {withdrawStatus && (
                <div
                  className={`rounded-2xl border px-4 py-3 text-sm font-semibold ${
                    withdrawStatus.type === 'error'
                      ? 'border-rose-200 bg-rose-50 text-rose-600'
                      : 'border-emerald-200 bg-emerald-50 text-emerald-700'
                  }`}
                >
                  {withdrawStatus.message}
                </div>
              )}

              {/* Withdraw Button */}
              <button
                type="submit"
                disabled={
                  isWithdrawing ||
                  !withdrawAmount ||
                  Number(withdrawAmount) <= 0 ||
                  Number(withdrawAmount) > feeAccount.walletBalance
                }
                className="w-full rounded-2xl bg-red-600 px-6 py-4 text-base font-semibold text-white transition hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isWithdrawing ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <ArrowDown size={18} />
                    Withdraw{' '}
                    {withdrawAmount
                      ? formatCurrency(parseFloat(withdrawAmount))
                      : ''}
                  </>
                )}
              </button>

              {/* Info Box */}
              <div className="flex items-start gap-3 rounded-2xl border border-gray-100 bg-gray-50 p-4 text-sm text-gray-700">
                <ShieldAlert className="h-5 w-5 text-gray-500" />
                <p>
                  Withdrawals are processed via Stripe payouts to the platform's
                  default bank account. Payouts typically arrive within 1-2
                  business days depending on your bank.
                </p>
              </div>

              <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-600 space-y-2">
                <div className="flex items-center gap-2 font-semibold text-gray-800">
                  <Info className="h-4 w-4 text-yellow-500" />
                  Withdrawal Information
                </div>
                <p>
                  Withdrawals are processed to the platform's connected bank
                  account via Stripe.
                </p>
                <p>
                  After submitting, the withdrawal will be processed and funds
                  will arrive in your bank account within 1-2 business days.
                </p>
              </div>
            </form>
          </div>
        )}
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Total Fees</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(stats.totalFees)}
              </p>
            </div>
            <div className="bg-yellow-100 rounded-full p-3">
              <DollarSign className="text-yellow-600" size={24} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Total Transactions</p>
              <p className="text-2xl font-bold text-gray-900">
                {stats.totalTransactions}
              </p>
            </div>
            <div className="bg-blue-100 rounded-full p-3">
              <FileText className="text-blue-600" size={24} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">This Month</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(stats.feesThisMonth)}
              </p>
            </div>
            <div className="bg-green-100 rounded-full p-3">
              <TrendingUp className="text-green-600" size={24} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Last Month</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(stats.feesLastMonth)}
              </p>
            </div>
            <div className="bg-purple-100 rounded-full p-3">
              <Calendar className="text-purple-600" size={24} />
            </div>
          </div>
        </div>
      </div>

      {/* Withdrawal Transactions Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Withdrawal Transactions
          </h2>
        </div>

        {withdrawals.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-gray-500">No withdrawal transactions found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    To
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Payout ID
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {withdrawals.map(withdrawal => (
                  <tr key={withdrawal.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(withdrawal.date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          Bank Account
                        </p>
                        <p className="text-sm text-gray-500">Stripe Payout</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {withdrawal.description}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {withdrawal.payoutId ? (
                        <span className="text-sm text-gray-600 font-mono">
                          {withdrawal.payoutId.substring(0, 8)}...
                          {withdrawal.simulated && (
                            <span className="text-xs text-yellow-600 ml-1">
                              (sim)
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">N/A</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-semibold text-red-600">
                      -{formatCurrency(withdrawal.amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          withdrawal.status === 'completed'
                            ? 'bg-green-100 text-green-800'
                            : withdrawal.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {withdrawal.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Fee Transactions Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden mt-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Fee Transactions
          </h2>
        </div>

        {transactions.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-gray-500">No fee transactions found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    From
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Request ID
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {transactions.map(transaction => (
                  <tr key={transaction.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(transaction.date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {transaction.payerInfo ? (
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {transaction.payerInfo.displayName}
                          </p>
                          <p className="text-sm text-gray-500">
                            {transaction.payerInfo.email}
                          </p>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">
                          Unknown User
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {transaction.description}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {transaction.requestId ? (
                        <span className="text-sm text-gray-600 font-mono">
                          {transaction.requestId.substring(0, 8)}...
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">N/A</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-semibold text-green-600">
                      +{formatCurrency(transaction.amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          transaction.status === 'completed'
                            ? 'bg-green-100 text-green-800'
                            : transaction.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {transaction.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Fees;
